import { useState } from "react";

import {
  Badge,
  Box,
  Button,
  HStack,
  Select,
  Table,
  Text,
  Tooltip,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import {
  AiGatewayFallbackLink,
  AiGatewayRoute,
  AiModel,
  aiGatewayService,
} from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

import { routeDisplayName, routeInheritanceHint } from "./routeLabel";

interface RoutingPanelProps {
  routes: AiGatewayRoute[];
  models: AiModel[];
  loading: boolean;
  onChanged: () => void;
}

const UNBOUND = "__unbound__";

/**
 * Per-route model bindings — the "Advanced Mode" of the two routing modes.
 *
 * Naming note: "Advanced Mode" here means a per-route binding. It is unrelated to the "Advanced
 * routing" wording that used to sit on the per-question switch in DifficultyPanel — that copy was
 * renamed to "per-question routing" so the two stop colliding.
 *
 * A binding is the most specific step in the resolution chain, so anything set here wins over the
 * difficulty tier map. Platform-plane routes (planner, decomposer, server generation) run on
 * StrikeUp's own models and are not billable to the company, so they render read-only; the API
 * rejects a write on one rather than relying on the UI to prevent it.
 *
 * The Fallback column is read-only on both tables: chains are configured by StrikeUp through the
 * internal-admin API. It is shown because it is what actually answers the turn when the bound model
 * is down, and a routing table that hides that is describing only the good day.
 */
const RoutingPanel = ({
  routes,
  models,
  loading,
  onChanged,
}: RoutingPanelProps) => {
  const { t } = useTranslation();
  const [savingRoute, setSavingRoute] = useState<string | null>(null);

  // The same model reached through two connectors is two different choices — price, latency and
  // availability all differ — so the connector is part of the label, not a hidden detail.
  const modelOptions = [
    { label: t("app.aiGateway.routing.inherit"), value: UNBOUND },
    ...models.map((model) => ({
      label: model.connectorName
        ? `${model.displayName} · ${model.connectorName}`
        : `${model.displayName} · ${model.provider}`,
      value: model.id,
    })),
  ];

  // A route can stay bound to a model that has since been retired: the catalog only offers active,
  // non-deprecated rows, and nothing rewrites a binding when a model leaves it. Without an option
  // carrying that id the native select renders blank, and the next save silently rebinds the route
  // to whatever the browser picks. The extra option is disabled — it says what the binding is
  // without offering it as a choice.
  const optionsFor = (currentModelId: string | null) =>
    currentModelId && !models.some((m) => m.id === currentModelId)
      ? [
          ...modelOptions,
          {
            label: t("app.aiGateway.retiredModel"),
            value: currentModelId,
            disabled: true,
          },
        ]
      : modelOptions;

  // The chain arrives ordered and already capped at the depth the executor will actually run, so
  // it is rendered exactly as given — an entry here is an attempt that will be made.
  const renderFallback = (
    chain: AiGatewayFallbackLink[],
    isReadOnly: boolean,
  ) => {
    if (chain.length === 0) {
      return (
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.fallback.none")}
        </Text>
      );
    }

    return (
      <VStack align="start" gap={1}>
        {chain.map((link) => (
          <HStack key={link.order} gap={2}>
            <Badge colorPalette="gray" size="sm">
              {t("app.aiGateway.fallback.attempt", { order: link.order })}
            </Badge>
            <Text fontSize="xs">
              {link.modelKey ?? t("app.aiGateway.fallback.modelInactive")}
            </Text>
            {link.connectorName && (
              <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                {link.connectorName}
              </Text>
            )}
          </HStack>
        ))}
        {!isReadOnly && chain[0].isPlatformManaged && (
          <Text fontSize="xs" color={getThemeColor("text.secondary")}>
            {t("app.aiGateway.fallback.platformManaged")}
          </Text>
        )}
      </VStack>
    );
  };

  const handleBind = async (route: AiGatewayRoute, modelId: string) => {
    const model = models.find((m) => m.id === modelId) ?? null;
    setSavingRoute(route.agentRoute);
    try {
      await aiGatewayService.upsertRoute(route.agentRoute, {
        aiConnectorId: model?.aiConnectorId ?? null,
        aiModelId: model?.id ?? null,
        isActive: true,
      });
      onChanged();
    } finally {
      setSavingRoute(null);
    }
  };

  const tenantRoutes = routes.filter((r) => !r.isReadOnly);
  const platformRoutes = routes.filter((r) => r.isReadOnly);

  return (
    <VStack align="stretch" gap={5}>
      <VStack align="start" gap={1}>
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.routing.subtitle")}
        </Text>
        {/* This tab writes on change while the Standard Mode tab has a Save button. Two tabs of
            one page behaving differently has to be stated, not discovered. */}
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.routing.savesImmediately")}
        </Text>
      </VStack>

      {!loading && routes.length === 0 && (
        <Text color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.routing.empty")}
        </Text>
      )}

      {tenantRoutes.length > 0 && (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" minW="1000px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.route")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  <Tooltip content={t("app.aiGateway.routing.tierColumnHelp")}>
                    <Text
                      as="span"
                      cursor="help"
                      borderBottomWidth="1px"
                      borderBottomStyle="dashed"
                    >
                      {t("app.aiGateway.table.tier")}
                    </Text>
                  </Tooltip>
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  <Tooltip content={t("app.aiGateway.routing.modelColumnHelp")}>
                    <Text
                      as="span"
                      cursor="help"
                      borderBottomWidth="1px"
                      borderBottomStyle="dashed"
                    >
                      {t("app.aiGateway.table.model")}
                    </Text>
                  </Tooltip>
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  <Tooltip
                    content={t("app.aiGateway.routing.fallbackColumnHelp")}
                  >
                    <Text
                      as="span"
                      cursor="help"
                      borderBottomWidth="1px"
                      borderBottomStyle="dashed"
                    >
                      {t("app.aiGateway.table.fallback")}
                    </Text>
                  </Tooltip>
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tenantRoutes.map((route) => (
                <Table.Row key={route.agentRoute}>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <Text fontWeight="medium">
                        {routeDisplayName(t, route)}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                      >
                        {route.agentRoute}
                      </Text>
                      {route.inheritsFrom && (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                          fontStyle="italic"
                        >
                          {routeInheritanceHint(t, route)}
                        </Text>
                      )}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette="purple" size="sm">
                      {t(`app.aiGateway.difficulty.tier.${route.tier}`)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell minW="280px">
                    <Select
                      size="sm"
                      value={[route.aiModelId ?? UNBOUND]}
                      onValueChange={(d: { value: string[] }) =>
                        handleBind(route, d.value[0])
                      }
                      items={optionsFor(route.aiModelId)}
                      disabled={savingRoute === route.agentRoute}
                    />
                  </Table.Cell>
                  <Table.Cell minW="220px">
                    {renderFallback(route.fallbackChain, route.isReadOnly)}
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={2} justify="flex-end">
                      {route.aiModelId && (
                        <Tooltip content={t("app.aiGateway.routing.resetHelp")}>
                          <Button
                            intent="ghost"
                            size="sm"
                            loading={savingRoute === route.agentRoute}
                            onClick={() => handleBind(route, UNBOUND)}
                          >
                            {t("app.aiGateway.routing.reset")}
                          </Button>
                        </Tooltip>
                      )}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}

      {platformRoutes.length > 0 && (
        <VStack align="stretch" gap={2}>
          <Text fontSize="sm" fontWeight="semibold">
            {t("app.aiGateway.routing.platformTitle")}
          </Text>
          <Text fontSize="sm" color={getThemeColor("text.secondary")}>
            {t("app.aiGateway.routing.platformSubtitle")}
          </Text>
          <Box overflowX="auto">
            <Table.Root size="sm" variant="outline" minW="820px">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    {t("app.aiGateway.table.route")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.aiGateway.table.tier")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.aiGateway.table.fallback")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.aiGateway.table.status")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {platformRoutes.map((route) => (
                  <Table.Row key={route.agentRoute}>
                    <Table.Cell>
                      <Text>{route.agentRoute}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette="purple" size="sm">
                        {t(`app.aiGateway.difficulty.tier.${route.tier}`)}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell minW="220px">
                      {renderFallback(route.fallbackChain, route.isReadOnly)}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette="gray" size="sm">
                        {t("app.aiGateway.table.managedByPlatform")}
                      </Badge>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </VStack>
      )}
    </VStack>
  );
};

export default RoutingPanel;
